import {
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  getFromStorage,
  getToken,
  getWarehouses,
  requestErrorHandler,
  saveToStorage,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";

const server = EnvSettings.server;
const STOCK_STATISTICS_SEARCH_ID = "stock_statistics_search";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getStockWarehouseName = (stockRow) => stockRow?.Warehouse?.name;

const getStockSearchText = (stockRow, warehouses) => {
  const warehouseName = getStockWarehouseName(stockRow);
  const warehouseLabel =
    warehouses?.find((warehouse) => warehouse.value === warehouseName)?.label ||
    "";
  return [
    stockRow?.StockItem?.sku,
    stockRow?.StockItem?.name,
    warehouseName,
    warehouseLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getWarehouseLabel = (warehouses, warehouseName) =>
  warehouses?.find((warehouse) => warehouse.value === warehouseName)?.label ||
  warehouseName ||
  "";

const formatQuantity = (value) =>
  toNumber(value).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });

const formatCurrency = (value, outputFormat) => {
  const numeric = toNumber(value);
  if (outputFormat === "json") {
    return numeric.toFixed(2);
  }
  return `${numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} EUR`;
};

const createValuationRow = (stockRow) => {
  const free = toNumber(stockRow?.free);
  const total = toNumber(stockRow?.total);
  const costPrice = toNumber(stockRow?.StockItem?.costPrice);
  const listPrice = toNumber(stockRow?.StockItem?.listPrice);

  return {
    ...stockRow,
    valuation: {
      freeCostValue: free * costPrice,
      costValue: total * costPrice,
      listValue: total * listPrice,
    },
  };
};

const buildTotals = (rows) =>
  rows.reduce(
    (summary, row) => ({
      rows: summary.rows + 1,
      warehouses: summary.warehouses.add(getStockWarehouseName(row)),
      freeQuantity: summary.freeQuantity + toNumber(row.free),
      totalQuantity: summary.totalQuantity + toNumber(row.total),
      freeCostValue:
        summary.freeCostValue + toNumber(row.valuation?.freeCostValue),
      costValue: summary.costValue + toNumber(row.valuation?.costValue),
      listValue: summary.listValue + toNumber(row.valuation?.listValue),
    }),
    {
      rows: 0,
      warehouses: new Set(),
      freeQuantity: 0,
      totalQuantity: 0,
      freeCostValue: 0,
      costValue: 0,
      listValue: 0,
    },
  );

const buildWarehouseRows = (rows) => {
  const warehouseRows = new Map();
  rows.forEach((row) => {
    const warehouseName = getStockWarehouseName(row);
    if (!warehouseRows.has(warehouseName)) {
      warehouseRows.set(warehouseName, {
        warehouse: warehouseName,
        rows: 0,
        freeQuantity: 0,
        totalQuantity: 0,
        freeCostValue: 0,
        costValue: 0,
        listValue: 0,
      });
    }
    const warehouseRow = warehouseRows.get(warehouseName);
    warehouseRow.rows += 1;
    warehouseRow.freeQuantity += toNumber(row.free);
    warehouseRow.totalQuantity += toNumber(row.total);
    warehouseRow.freeCostValue += toNumber(row.valuation?.freeCostValue);
    warehouseRow.costValue += toNumber(row.valuation?.costValue);
    warehouseRow.listValue += toNumber(row.valuation?.listValue);
  });
  return [...warehouseRows.values()];
};

export default function StockStatistics() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [stock, setStock] = useState([]);
  const [warehouseLocation, setWarehouseLocation] = useState();
  const [selectedWarehouses, setSelectedWarehouses] = useState();
  const [searchValue, setSearchValue] = useState("");

  const itemHeadCells = [
    {
      id: "StockItem.sku",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.sku"),
    },
    {
      id: "StockItem.name",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.name"),
    },
    {
      id: "Warehouse.name",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.warehouse"),
    },
    {
      id: "free",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.free"),
    },
    {
      id: "total",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.total"),
    },
    {
      id: "StockItem.costPrice",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.costPrice"),
    },
    {
      id: "valuation.freeCostValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.freeCostValue"),
    },
    {
      id: "valuation.costValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.stockCostValue"),
    },
    {
      id: "StockItem.listPrice",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.listPrice"),
    },
    {
      id: "valuation.listValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.stockListValue"),
    },
  ];

  const warehouseHeadCells = [
    {
      id: "warehouse",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.warehouse"),
    },
    {
      id: "rows",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.stockRows"),
    },
    {
      id: "freeQuantity",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.totalFreeQuantity"),
    },
    {
      id: "totalQuantity",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.totalStockQuantity"),
    },
    {
      id: "freeCostValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.freeCostValue"),
    },
    {
      id: "costValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.totalCostValue"),
    },
    {
      id: "listValue",
      numeric: true,
      disablePadding: false,
      label: t("stockModule.totalListValue"),
    },
  ];

  useEffect(() => {
    const savedSearch = getFromStorage(STOCK_STATISTICS_SEARCH_ID);
    if (!savedSearch) {
      return;
    }
    try {
      const savedFilters = JSON.parse(savedSearch);
      if (typeof savedFilters.searchValue === "string") {
        setSearchValue(savedFilters.searchValue);
      }
      if (Array.isArray(savedFilters.selectedWarehouses)) {
        setSelectedWarehouses(savedFilters.selectedWarehouses);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    };
    fetch(server + "/get_stock_quantity", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setStock(Array.isArray(result.stock) ? result.stock : []);
        }
      })
      .catch(() => {
        setStock([]);
      })
      .finally(() => {
        setLoading(false);
      });
    getWarehouses(setWarehouseLocation, dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (selectedWarehouses !== undefined || !warehouseLocation?.length) {
      return;
    }
    setSelectedWarehouses(warehouseLocation.map((warehouse) => warehouse.value));
  }, [selectedWarehouses, warehouseLocation]);

  const updateFilter = (newValue, key, setter) => {
    let savedFilters = {};
    const savedSearch = getFromStorage(STOCK_STATISTICS_SEARCH_ID);
    if (savedSearch) {
      try {
        savedFilters = JSON.parse(savedSearch);
      } catch (error) {}
    }
    saveToStorage(
      STOCK_STATISTICS_SEARCH_ID,
      JSON.stringify({
        ...savedFilters,
        [key]: newValue,
      }),
    );
    setter(newValue);
  };

  const valuationRows = useMemo(
    () => (stock || []).map((stockRow) => createValuationRow(stockRow)),
    [stock],
  );

  const filteredRows = useMemo(() => {
    if (!selectedWarehouses) {
      return [];
    }
    let rows = valuationRows.filter((stockRow) =>
      selectedWarehouses.includes(getStockWarehouseName(stockRow)),
    );
    if (searchValue.trim()) {
      rows = rows.filter((stockRow) =>
        getStockSearchText(stockRow, warehouseLocation).includes(
          searchValue.trim().toLowerCase(),
        ),
      );
    }
    return rows;
  }, [searchValue, selectedWarehouses, valuationRows, warehouseLocation]);

  const totals = useMemo(() => buildTotals(filteredRows), [filteredRows]);
  const warehouseRows = useMemo(
    () => buildWarehouseRows(filteredRows),
    [filteredRows],
  );

  const selectedWarehouseList = selectedWarehouses || [];
  const warehouseOptions = warehouseLocation || [];
  const allWarehouseValues = warehouseOptions.map(
    (warehouse) => warehouse.value,
  );

  const quantityFormatter = {
    formatReturnsElement: true,
    format: (data, outputFormat) =>
      outputFormat === "json" ? toNumber(data) : formatQuantity(data),
  };

  const currencyFormatter = {
    formatReturnsElement: true,
    format: (data, outputFormat) => formatCurrency(data, outputFormat),
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/stock">
            {t("stock.header")}
          </Link>
          <Typography>{t("stockModule.stockStatistics")}</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("stockModule.stockStatistics")}</Title>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={loading ? t("common.loading") : t("search")}
                placeholder={t("stockModule.searchPlaceholder")}
                variant="outlined"
                disabled={loading}
                value={searchValue}
                InputLabelProps={{ shrink: !!searchValue ? true : undefined }}
                onChange={(e) => {
                  updateFilter(
                    e.target.value || "",
                    "searchValue",
                    setSearchValue,
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography>{t("stockModule.warehouse")}</Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      updateFilter(
                        allWarehouseValues,
                        "selectedWarehouses",
                        setSelectedWarehouses,
                      );
                    }}
                  >
                    {t("stockModule.selectAllWarehouses")}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      updateFilter(
                        [],
                        "selectedWarehouses",
                        setSelectedWarehouses,
                      );
                    }}
                  >
                    {t("stockModule.clearWarehouses")}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  {warehouseLocation ? (
                    warehouseOptions.map((warehouse) => (
                      <FormControlLabel
                        key={warehouse.value}
                        control={
                          <Checkbox
                            color="primary"
                            style={{
                              color: warehouse.color || "",
                            }}
                            checked={selectedWarehouseList.includes(
                              warehouse.value,
                            )}
                            onChange={(e, checked) => {
                              updateFilter(
                                checked
                                  ? [
                                      ...selectedWarehouseList,
                                      warehouse.value,
                                    ]
                                  : selectedWarehouseList.filter(
                                      (value) => value !== warehouse.value,
                                    ),
                                "selectedWarehouses",
                                setSelectedWarehouses,
                              );
                            }}
                          />
                        }
                        label={warehouse.label}
                      />
                    ))
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">{t("stockModule.stockRows")}</Typography>
          <Typography variant="h5">{totals.rows}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">
            {t("stockModule.selectedWarehouseCount")}
          </Typography>
          <Typography variant="h5">{totals.warehouses.size}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">
            {t("stockModule.totalStockQuantity")}
          </Typography>
          <Typography variant="h5">{formatQuantity(totals.totalQuantity)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">
            {t("stockModule.totalCostValue")}
          </Typography>
          <Typography variant="h5">{formatCurrency(totals.costValue)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">
            {t("stockModule.freeCostValue")}
          </Typography>
          <Typography variant="h5">
            {formatCurrency(totals.freeCostValue)}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Paper className={classes.paper}>
          <Typography variant="caption">
            {t("stockModule.totalListValue")}
          </Typography>
          <Typography variant="h5">{formatCurrency(totals.listValue)}</Typography>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("stockModule.warehouseStatistics")}</Title>
          {loading ? (
            <CircularProgress />
          ) : (
            <SortableTable
              header={
                "stock_statistics_by_warehouse_" +
                new Date().toISOString().substring(0, 10)
              }
              alternateColors={true}
              downloadButton={true}
              headCells={warehouseHeadCells}
              defaultSortRow={"costValue"}
              defaultOrder={"desc"}
              tableData={warehouseRows}
              rowsPerPage={25}
              getRowStyle={(data, index) => ({
                cursor: "default",
                ...(index % 2 === 0
                  ? { backgroundColor: "rgba(245, 245, 245, 1)" }
                  : {}),
              })}
              specialDataFormatting={[
                {
                  id: "warehouse",
                  format: (data) => getWarehouseLabel(warehouseLocation, data),
                },
                {
                  id: "rows",
                  ...quantityFormatter,
                },
                {
                  id: "freeQuantity",
                  ...quantityFormatter,
                },
                {
                  id: "totalQuantity",
                  ...quantityFormatter,
                },
                {
                  id: "freeCostValue",
                  ...currencyFormatter,
                },
                {
                  id: "costValue",
                  ...currencyFormatter,
                },
                {
                  id: "listValue",
                  ...currencyFormatter,
                },
              ]}
            />
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("stockModule.itemValuation")}</Title>
          {loading ? (
            <CircularProgress />
          ) : (
            <SortableTable
              header={
                "stock_statistics_items_" +
                new Date().toISOString().substring(0, 10)
              }
              alternateColors={true}
              downloadButton={true}
              headCells={itemHeadCells}
              defaultSortRow={"valuation.costValue"}
              defaultOrder={"desc"}
              tableData={filteredRows}
              rowsPerPage={200}
              getRowStyle={(data, index) => ({
                cursor: "default",
                ...(index % 2 === 0
                  ? { backgroundColor: "rgba(245, 245, 245, 1)" }
                  : {}),
              })}
              specialDataFormatting={[
                {
                  id: "Warehouse.name",
                  format: (data) => getWarehouseLabel(warehouseLocation, data),
                },
                {
                  id: "free",
                  ...quantityFormatter,
                },
                {
                  id: "total",
                  ...quantityFormatter,
                },
                {
                  id: "StockItem.costPrice",
                  ...currencyFormatter,
                },
                {
                  id: "valuation.freeCostValue",
                  ...currencyFormatter,
                },
                {
                  id: "valuation.costValue",
                  ...currencyFormatter,
                },
                {
                  id: "StockItem.listPrice",
                  ...currencyFormatter,
                },
                {
                  id: "valuation.listValue",
                  ...currencyFormatter,
                },
              ]}
            />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
